/**
 *
 * SecuritySetUpLayout
 *
 */

import { stylize } from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import withIntl from "utils/withIntl";

import AuthenticatorWidget from "components/AuthenticatorWidget";
import CentricLayout from "components/CentricLayout";
import Logo from "images/EnvLogo";
import Shield from "images/Shield";
import UserIconCircle from "images/UserIconCircle";
import { AuthenticatorConfiguration } from "types/Mfa";
import messages from "./messages";
import SkeletonLoader from "./skeletonLoader";
import styles, { Styles } from "./styles";
import { SuccessMessagePlaceholder } from "./successFooterMessage";
import { SecurityMethodTypes } from "./types";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */

  loadSecurityMethods: Function;

  goToAppPhoneScreen: Function;
  goToEmailOtpScreen: () => void;
  goToOktaVerifyScreen: Function;
  goToAppAuthenticatorScreen: Function;
  goToEnvAppScreen: () => void;

  loadQRCode: Function;

  securityMethods: Array<SecurityMethodTypes>;
  isLoading: boolean;
  username: string;

  setSuccessMsgBoolean: Function;
  hasSuccessMsgSeen: boolean;
  clearGlobalUserInfo: Function;
  goToLoginScreen: Function;
}

export interface SecuritySetUpLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {
  loadSecurityMethods: () => {},
  goToAppPhoneScreen: () => {},
  goToEmailOtpScreen: () => {},
  goToOktaVerifyScreen: () => {},
  goToAppAuthenticatorScreen: () => {},
  goToEnvAppScreen: () => {},
  loadQRCode: () => {},
  securityMethods: [],
  isLoading: false,
  username: "",
  setSuccessMsgBoolean: () => {},
  hasSuccessMsgSeen: false,
  clearGlobalUserInfo: () => {},
  goToLoginScreen: () => {},
};

type Props = InjectedProps & SecuritySetUpLayoutProps;

const SecuritySetUpLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    loadSecurityMethods,
    securityMethods,
    goToAppPhoneScreen,
    goToEmailOtpScreen,
    goToOktaVerifyScreen,
    goToAppAuthenticatorScreen,
    goToEnvAppScreen,
    isLoading,
    username,
  } = props;

  const [methods, setMethods] = useState(securityMethods);

  const canLogin = useMemo(
    () => securityMethods.some((method) => method.enabled),
    [securityMethods]
  );

  useEffect(() => {
    loadSecurityMethods();
  }, []);

  useEffect(() => {
    setMethods(securityMethods);
  }, [securityMethods]);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout>
        <div>
          <Logo />
          <div className={classes.shield}>
            <Shield />
          </div>
          <div className={classnames(classes.header4, "header-font-family")}>
            {formatMessage(messages.header)}
          </div>
          <div className={classes.usernameSection}>
            <UserIconCircle />
            <span className="header-text-font-family">{username}</span>
          </div>
          <div className={classnames(classes.desc, "header-font-family")}>
            {formatMessage(messages.desc)}
          </div>

          {!methods.length && <SkeletonLoader />}

          {methods
            .filter((method) => method.type !== "passkey")
            .map((method) => (
              <AuthenticatorWidget
                type={method.type}
                configurationStatus={
                  method.enabled
                    ? AuthenticatorConfiguration.Done
                    : AuthenticatorConfiguration.Initial
                }
                goToAppPhoneScreen={goToAppPhoneScreen}
                goToEmailOtpScreen={goToEmailOtpScreen}
                goToOktaVerifyScreen={goToOktaVerifyScreen}
                goToAppAuthenticatorScreen={goToAppAuthenticatorScreen}
                goToEnvAppScreen={goToEnvAppScreen}
                isLoading={isLoading}
                key={method.internalId}
              />
            ))}
          {canLogin && (
            <SuccessMessagePlaceholder methods={securityMethods} {...props} />
          )}
        </div>
      </CentricLayout>
    </div>
  );
};

SecuritySetUpLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(SecuritySetUpLayout)
);
